import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";

const ShoppingListItemCard = ({ item, onStatusChange, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const displayName =
    item.name || item.customName || item.label || "Unnamed Item";

  const [noteDraft, setNoteDraft] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);

  const handleMarkBought = () => {
    onStatusChange({ status: "bought", driverNote: "" });
  };

  const handleStartNote = () => {
    setIsEditingNote(true);
  };

  const handleSaveNote = () => {
    if (!noteDraft.trim()) return alert("Please enter a reason.");
    onStatusChange({ status: "not_available", driverNote: noteDraft });
    setIsEditingNote(false);
    setNoteDraft("");
  };

  const handleUndo = () => {
    onStatusChange({ status: "pending", driverNote: "" });
    setIsEditingNote(false);
    setNoteDraft("");
  };

  const showActionsInline =
    !isEditingNote && item.status === "pending" && !item.driverNote;

  return (
    <Box
      sx={{
        mb: 1,
        px: 1,
        py: 0.75,
        border: "1px solid #ccc",
        borderRadius: 1,
        backgroundColor: item.status === "bought" ? "#f0f0f0" : "#fff",
        opacity: item.status === "bought" ? 0.5 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        flexWrap="wrap"
        spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontWeight={600}
            fontSize={13}
            sx={{ wordBreak: "break-word" }}>
            {displayName}
          </Typography>
          <Typography fontSize={11} color="text.secondary">
            Qty: {item.quantity} {item.unit}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          gap={0.5}>
          {/* === Compact inline actions === */}
          {showActionsInline && (
            <>
              <Tooltip title="Bought">
                <IconButton size="small" onClick={handleMarkBought}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Not Available">
                <IconButton size="small" onClick={handleStartNote}>
                  <CancelIcon color="error" fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove Item">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(item._id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* === Expanded reason input === */}
          {(isEditingNote || item.status === "not_available") && (
            <>
              {isEditingNote ? (
                <>
                  <TextField
                    size="small"
                    placeholder="Reason"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    sx={{ width: 140 }}
                    autoFocus
                  />
                  <Tooltip title="Save Reason">
                    <IconButton size="small" onClick={handleSaveNote}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Undo">
                    <IconButton size="small" onClick={handleUndo}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  {item.driverNote && (
                    <Tooltip title={item.driverNote}>
                      <Chip
                        label={item.driverNote}
                        size="small"
                        variant="outlined"
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="Undo">
                    <IconButton size="small" onClick={handleUndo}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          )}

          {/* Bought State */}
          {!isEditingNote && item.status === "bought" && (
            <>
              <Chip label="Bought" size="small" color="success" />
              <Tooltip title="Undo">
                <IconButton size="small" onClick={handleUndo}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default ShoppingListItemCard;
